import { Avatar, AvatarFallback, AvatarImage } from "../avatar";

/**
 * Up to two initials, or "?" when there is no name to take them from.
 *
 * The fallback matters more than it looks: an account with no picture — every
 * password account until it uploads one — renders an empty grey circle without
 * it, because Radix draws nothing for an empty image src.
 */
// Exported alongside UserAvatar for reuse; unused elsewhere in src/ as of
// this writing.
// eslint-disable-next-line react-refresh/only-export-components
export function initials(name: string | null | undefined) {
  const trimmed = (name || "").trim();
  if (!trimmed) return "?";
  return trimmed
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * A person's avatar, with initials standing in until there is an image.
 *
 * `picture` is whatever the API resolved: an uploaded avatar's path on this
 * API, or the identity provider's URL.
 */
export default function UserAvatar({
  name,
  picture,
  className,
  fallbackClassName = "text-[10px] font-medium",
}: {
  name: string | null | undefined;
  picture: string | null | undefined;
  className?: string;
  fallbackClassName?: string;
}) {
  return (
    <Avatar className={className}>
      <AvatarImage alt={name || "User avatar"} src={picture || undefined} />
      <AvatarFallback className={fallbackClassName}>
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  );
}
